"use client";

import { useEffect } from "react";

const MobileFilterModal = ({
  isOpen,
  onClose,
  filters,
  onFiltersChange,
  onApplyFilters,
}) => {
  const handleGenderChange = (gender) => {
    onFiltersChange({ ...filters, gender });
  };

  const handleAgeChange = (age) => {
    onFiltersChange({ ...filters, age });
  };

  const handleDistanceMinChange = (distanceMin) => {
    if (distanceMin <= filters.distanceMax - 1) {
      onFiltersChange({ ...filters, distanceMin });
    }
  };

  const handleDistanceMaxChange = (distanceMax) => {
    if (distanceMax >= filters.distanceMin + 1) {
      onFiltersChange({ ...filters, distanceMax });
    }
  };

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const handleContinue = () => {
    onApplyFilters();
    onClose();
  };

  // Prevent body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }

    return () => {
      document.body.style.overflow = "unset";
    };
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-end transition-opacity duration-300"
      onClick={handleOverlayClick}
    >
      <div
        className={`w-full bg-white rounded-t-3xl p-6 transform transition-transform duration-300 ease-out ${
          isOpen ? "translate-y-0" : "translate-y-full"
        }`}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Handle */}
        <div className="flex justify-center mb-6">
          <div
            className="w-12 h-1.5 bg-gray-300 rounded-full cursor-pointer"
            onClick={onClose}
          />
        </div>

        {/* Gender Selection */}
        <div className="mb-8">
          <div className="flex gap-3">
            {["Boy", "Girl", "Both"].map((gender) => (
              <button
                key={gender}
                onClick={() => handleGenderChange(gender)}
                className={`px-6 py-3 rounded-full font-medium transition-all duration-200 ${
                  filters.gender === gender
                    ? "bg-black text-white shadow-lg scale-105"
                    : "bg-gray-100 text-gray-700 border border-gray-200 hover:bg-gray-200 hover:scale-102"
                }`}
              >
                {gender}
              </button>
            ))}
          </div>
        </div>

        {/* Age Slider */}
        <div className="mb-8">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-semibold text-gray-900">Age</h3>
            <span className="text-lg font-semibold text-[#7B189F]">
              {filters.age}
            </span>
          </div>
          <div className="relative px-2">
            <div className="relative h-2 bg-gray-200 rounded-full">
              <div
                className="absolute h-2 bg-[#7B189F] rounded-full transition-all duration-200"
                style={{ width: `${((filters.age - 18) / (50 - 18)) * 100}%` }}
              />
              <input
                type="range"
                min="18"
                max="50"
                value={filters.age}
                onChange={(e) =>
                  handleAgeChange(Number.parseInt(e.target.value))
                }
                className="absolute inset-0 w-full h-2 opacity-0 cursor-pointer"
              />
              <div
                className="absolute w-5 h-5 bg-[#7B189F] border-2 border-white rounded-full shadow-lg transform -translate-y-1.5 transition-all duration-200 hover:scale-110"
                style={{
                  left: `calc(${
                    ((filters.age - 18) / (50 - 18)) * 100
                  }% - 10px)`,
                }}
              />
            </div>
          </div>
        </div>

        {/* Distance Slider */}
        <div className="mb-8">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-semibold text-gray-900">Distance</h3>
            <span className="text-lg font-semibold text-[#7B189F]">
              {filters.distanceMin}-{filters.distanceMax} km
            </span>
          </div>
          <div className="relative px-2">
            <div className="relative h-2 bg-gray-200 rounded-full">
              <div
                className="absolute h-2 bg-[#7B189F] rounded-full transition-all duration-200"
                style={{
                  left: `${((filters.distanceMin - 1) / (50 - 1)) * 100}%`,
                  width: `${
                    ((filters.distanceMax - filters.distanceMin) / (50 - 1)) *
                    100
                  }%`,
                }}
              />

              <input
                type="range"
                min="1"
                max="50"
                value={filters.distanceMin}
                onChange={(e) =>
                  handleDistanceMinChange(Number.parseInt(e.target.value))
                }
                className="absolute inset-0 w-full h-2 opacity-0 cursor-pointer"
              />

              <input
                type="range"
                min="1"
                max="50"
                value={filters.distanceMax}
                onChange={(e) =>
                  handleDistanceMaxChange(Number.parseInt(e.target.value))
                }
                className="absolute inset-0 w-full h-2 opacity-0 cursor-pointer"
              />

              <div
                className="absolute w-5 h-5 bg-[#7B189F] border-2 border-white rounded-full shadow-lg transform -translate-y-1.5 transition-all duration-200 hover:scale-110 z-10"
                style={{
                  left: `calc(${
                    ((filters.distanceMin - 1) / (50 - 1)) * 100
                  }% - 10px)`,
                }}
              />

              <div
                className="absolute w-5 h-5 bg-[#7B189F] border-2 border-white rounded-full shadow-lg transform -translate-y-1.5 transition-all duration-200 hover:scale-110 z-10"
                style={{
                  left: `calc(${
                    ((filters.distanceMax - 1) / (50 - 1)) * 100
                  }% - 10px)`,
                }}
              />
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex gap-3">
          <button
            onClick={onClose}
            className="flex-1 bg-gray-100 hover:bg-gray-200 text-gray-700 font-semibold py-4 rounded-2xl text-lg transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleContinue}
            className="flex-1 bg-[#7B189F] hover:bg-purple-800 text-white font-semibold py-4 rounded-2xl text-lg transition-colors shadow-lg hover:shadow-xl"
          >
            Continue
          </button>
        </div>
      </div>
    </div>
  );
};

export default MobileFilterModal;
